import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { searchEmployeesFromFintechPapi } from '../../../../api/papi-service';
import { logger, handleError } from '../../../../logger';
import { Employee } from '../interfaces/interface';
import { areSetsEqual } from '../utils/employeeUtils';
import { PapiEmployeeMapper } from '../utils/papiEmployeeMapper';

interface UseEmployeeSearchConfig {
  debounceMs?: number;
  minCharacters?: number;
  maxResults?: number;
  existingEmails?: Set<string>;
}

interface EmployeeSearchResult extends Employee {
  isAlreadyAdded?: boolean;
}

interface UseEmployeeSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: EmployeeSearchResult[];
  isSearching: boolean;
  hasError: boolean;
  clearResults: () => void;
  currentPage: number;
  totalCount: number;
  hasMore: boolean;
  goToPage: (pageNumber: number) => void;
}

export const useEmployeeSearch = ({
  debounceMs = 500,
  minCharacters = 2,
  maxResults = 20,
  existingEmails = new Set()
}: UseEmployeeSearchConfig = {}): UseEmployeeSearchReturn => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Employee[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageTokens, setPageTokens] = useState<Map<number, string>>(new Map());

  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSearchQueryRef = useRef<string>('');
  const prevEmailsRef = useRef<Set<string>>(existingEmails);

  const stableExistingEmails = useMemo(() => {
    if (areSetsEqual(prevEmailsRef.current, existingEmails)) {
      return prevEmailsRef.current;
    }
    prevEmailsRef.current = new Set(existingEmails);
    return prevEmailsRef.current;
  }, [existingEmails]);

  const clearResults = useCallback(() => {
    setSearchResults([]);
    setHasError(false);
    setCurrentPage(1);
    setTotalCount(0);
    setHasMore(false);
    setPageTokens(new Map());
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const performSearch = useCallback(
    async (query: string, pageNumber: number, nextToken?: string) => {
      abortControllerRef.current = new AbortController();

      setIsSearching(true);
      setHasError(false);

      try {
        logger.info('Searching employees', { query, pageNumber });
        const response = await searchEmployeesFromFintechPapi(query, maxResults, nextToken);

        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setTotalCount(response.totalCount ?? 0);
        setHasMore(response.hasMore ?? false);

        if (response.nextToken && response.hasMore) {
          setPageTokens((prev) => {
            const newMap = new Map(prev);
            newMap.set(pageNumber + 1, response.nextToken!);
            return newMap;
          });
        }

        if (response.employees && response.employees.length > 0) {
          const results: Employee[] = PapiEmployeeMapper.mapToEmployees(response.employees);

          const resultsWithStatus: EmployeeSearchResult[] = results.map((emp: Employee) => ({
            ...emp,
            isAlreadyAdded: stableExistingEmails.has(emp.employee_internal_email_address?.toLowerCase() || '')
          }));

          setSearchResults(resultsWithStatus);
          setCurrentPage(pageNumber);
        } else {
          setSearchResults([]);
          setCurrentPage(1);
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          const errorMessage = 'Employee search failed';
          handleError(errorMessage, error);
          logger.warn(errorMessage, { error });
          setSearchResults([]);
          setHasError(true);
          setCurrentPage(1);
          setTotalCount(0);
          setHasMore(false);
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          setIsSearching(false);
        }
        abortControllerRef.current = null;
      }
    },
    [maxResults, stableExistingEmails]
  );

  const goToPage = useCallback(
    async (pageNumber: number) => {
      if (isSearching || !searchQuery) return;

      if (pageNumber < 1) return;

      if (pageNumber > 1 && !pageTokens.has(pageNumber)) {
        logger.warn(`No token available for page ${pageNumber}`);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const nextToken = pageNumber > 1 ? pageTokens.get(pageNumber) : undefined;

      await performSearch(searchQuery, pageNumber, nextToken);
    },
    [isSearching, searchQuery, pageTokens, performSearch]
  );

  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < minCharacters) {
      clearResults();
      lastSearchQueryRef.current = '';
      return;
    }

    if (searchQuery === lastSearchQueryRef.current) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setCurrentPage(1);
    setPageTokens(new Map());

    const searchTimeout = setTimeout(async () => {
      if (searchQuery !== lastSearchQueryRef.current) {
        lastSearchQueryRef.current = searchQuery;
        await performSearch(searchQuery, 1);
      }
    }, debounceMs);

    return () => {
      clearTimeout(searchTimeout);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [searchQuery, minCharacters, debounceMs, performSearch, clearResults]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    hasError,
    clearResults,
    currentPage,
    totalCount,
    hasMore,
    goToPage
  };
};
